import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DOCUMENT, inject, Input, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { SALES } from '@rma/generic/domain/types';
import {
  GOOGLE_MAPS_STATIC_MARKER,
  MapMarkerStatus,
  MapMarkerType,
  RmaMapMarker,
  RmaMapMarkerConfig,
  RmaMapMarkerDetail,
} from '@rma/generic/maps/domain';
import { CoordsToMapMarkerPipe, MapMarkerService } from '@rma/generic/maps/util';
import { fromPartial } from '@total-typescript/shoehorn';
import { uniqBy } from 'lodash-es';
import { BehaviorSubject, Observable } from 'rxjs';
import { MarketReportCampaign } from '../domain';
import { BackToMapDirective } from '../ui-back-to-map/back-to-map.directive';
import { MarketReportComparablePropertiesMapComponent } from './ui-market-report-comparable-properties-map/market-report-comparable-properties-map.component';
import { MarketReportComparablePropertyTilesComponent } from './ui-market-report-comparable-property-tiles/market-report-comparable-property-tiles.component';

@Component({
  selector: 'rma-market-report-comparable-properties',
  templateUrl: './market-report-comparable-properties.component.html',
  styleUrls: ['./market-report-comparable-properties.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    BackToMapDirective,
    MarketReportComparablePropertiesMapComponent,
    MarketReportComparablePropertyTilesComponent,
    AsyncPipe,
    TranslocoPipe,
  ],
  providers: [CoordsToMapMarkerPipe, MapMarkerService],
})
export class MarketReportComparablePropertiesComponent implements OnChanges {
  @Input()
  primaryMapMarker: RmaMapMarker | null | undefined;

  @Input()
  comparableCampaigns: MarketReportCampaign[] | null | undefined = [];

  @Input()
  isPrintMode: boolean | null = false;

  @Input()
  printedUrl: string | undefined;

  private readonly mapMarkerService = inject(MapMarkerService);
  private readonly coordsToMapMarkerPipe = inject(CoordsToMapMarkerPipe);
  private readonly document = inject(DOCUMENT);
  private readonly markerType: MapMarkerType = 'CompletedListing';

  markers$: Observable<RmaMapMarker[]>;
  selectedItemId?: string;
  selectedElement: HTMLElement | null;

  private markersSource = new BehaviorSubject<RmaMapMarker[]>([]);

  constructor() {
    this.markers$ = this.markersSource.asObservable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { comparableCampaigns } = changes;

    if (comparableCampaigns && this.comparableCampaigns) {
      const markers = this.mapToRmaMapMarkers(this.comparableCampaigns, this.isPrintMode ?? false);

      if (markers.length) {
        if (this.primaryMapMarker) {
          const primaryMarker = {
            ...this.primaryMapMarker,
            content: this.mapMarkerService.getContent(GOOGLE_MAPS_STATIC_MARKER),
          };

          markers.unshift(primaryMarker);
        }

        const coordCampaigns = markers.map((marker) => ({
          ...marker,
          latlon: JSON.stringify(marker.detail.coords),
        }));

        const uniqueMarkers = uniqBy(coordCampaigns, 'latlon');

        this.markersSource.next(uniqueMarkers);
      }
    }
  }

  onMarkerSelected(itemId: string | string[]) {
    const selectedItemId = Array.isArray(itemId) ? itemId[0] : itemId;
    this.setSelectedItemId(selectedItemId);
    this.markersSource.next(this.setSelectedMarker(this.markersSource.value, selectedItemId));
  }

  setSelectedItemId(itemId: string) {
    this.selectedItemId = this.selectedItemId !== itemId ? itemId : undefined;

    if (this.selectedItemId) {
      this.selectedElement = this.document.getElementById(itemId);

      if (this.selectedElement) {
        this.selectedElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }

  private mapToRmaMapMarkers(comparableCampaigns: MarketReportCampaign[], isStaticMap: boolean): RmaMapMarker[] {
    return comparableCampaigns
      .filter((c) => (c.latitude ?? 0) !== 0 && (c.longitude ?? 0) !== 0)
      .map((campaign, i) => this.mapToRmaMapMarker(campaign, isStaticMap, i + 1));
  }

  private mapToRmaMapMarker(campaign: MarketReportCampaign, isStaticMap: boolean, markerIndex: number): RmaMapMarker {
    if (isStaticMap) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return this.coordsToMapMarkerPipe.transform(
        {
          lat: campaign.latitude ?? 0,
          lon: campaign.longitude ?? 0,
        },
        { status: 'standard', mapType: this.markerType },
      )!;
    }

    const detail: RmaMapMarkerDetail = {
      id: campaign.campaignCode,
      combinedHash: 0,
      title: campaign.fullAddress,
      codes: [campaign.campaignCode],
      coords: {
        lat: campaign.latitude ?? 0,
        lon: campaign.longitude ?? 0,
      },
      markerCount: 1,
      status: 'standard',
      hovered: false,
      verticalType: SALES,
      info: [],
      markerType: this.markerType,
    };

    const { content, zIndex, gmpClickable } = this.getDefaultMarker(markerIndex);

    return {
      detail,
      position: { lat: detail.coords.lat, lng: detail.coords.lon },
      title: detail.title,
      content,
      zIndex,
      options: {
        gmpClickable,
      },
    };
  }

  private setSelectedMarker(markers: RmaMapMarker[], itemId: string): RmaMapMarker[] {
    return markers.map((marker, i) => {
      if (marker.detail.status === 'active') {
        const { content } = this.getDefaultMarker(marker.zIndex ?? i, 'viewed');
        return {
          ...marker,
          content,
          detail: { ...marker.detail, status: 'viewed' },
          options: { ...marker.options, gmpClickable: true },
        };
      }

      if (marker.detail.id === itemId) {
        return {
          ...marker,
          content: this.mapMarkerService.transform(fromPartial({ markerType: this.markerType }), 'active', marker.zIndex ?? i).content,
          options: { ...marker.options, gmpClickable: false },
          detail: { ...marker.detail, status: 'active' },
        };
      }

      return marker;
    });
  }

  private getDefaultMarker(markerIndex: number, status: MapMarkerStatus = 'standard'): RmaMapMarkerConfig {
    return {
      ...this.mapMarkerService.transform(fromPartial({ markerType: this.markerType }), status, markerIndex),
      gmpClickable: true,
    };
  }
}
